import cloneDeep from 'lodash/cloneDeep';
import { Maybe, doesExist, isUndefined } from 'utils/tsHelpers';
import { mapByVocabEvent } from 'modules/predictiveGoals/vocabUtils';
import {
  DEFAULT_DURATION_SERIALIZED,
  TEMPLATE_EVENT_POPOVER_HIGH_USAGE,
  TEMPLATE_EVENT_POPOVER_LOW_USAGE,
  TEMPLATE_EVENT_USAGE_THRESHOLD,
} from 'modules/predictiveGoals/constants';
import {
  Event,
  GoalQueryClause,
  InternalTemplateGoalQueriesMap,
  InternalTemplates,
  PredictiveAudiencesVocabulary,
  PredictiveGoalQuery,
  QueryBuilderOrClause,
  TemplateEventPopoverContent,
  TemplateInternalDeltaNames,
  TemplateQuery,
  UserSelectedInternalTemplate,
  VocabEvents,
  VocabularyEvent,
  VocabularyResponse,
} from 'modules/predictiveGoals/types';
import { GOAL_TYPES } from 'modules/predictiveGoals/components/queryBuilder/constants';
import { createEventCondition } from 'modules/predictiveGoals/components/queryBuilder/utils/queryHelpers';
import {
  getNextPropertyComparison,
  getPropertyDefaultComparator,
} from 'modules/predictiveGoals/components/queryBuilder/utils/utils';

export const emptyGoal: PredictiveGoalQuery = {
  and_clauses: [],
};

export const emptyOrClause: GoalQueryClause = {
  name: '',
  type: '',
};

export const emptyEvent: Event = {
  __typename: '',
  deltaName: '',
  displayName: '',
  type: VocabEvents.CUSTOM,
  userCount13Week: 0,
  userCount1Month: 0,
};

export type EventMatch = Maybe<Event>;

export type TemplateCreationData = Maybe<{ data: TemplateQuery }>;

export const isGoalSystemReady = (vocab: VocabularyResponse, templateData: TemplateCreationData) =>
  doesExist(vocab) && doesExist(templateData);

/**
@abstract (PredictiveAudiencesVocabulary, Event) -> EventMatch
*/
export const getVocabEventByDeltaNameAndEventType = (
  goalVocabData: PredictiveAudiencesVocabulary,
  templateEvent: Pick<Event, 'type' | 'deltaName'>,
): EventMatch => {
  const vocabEventsByType = mapByVocabEvent(goalVocabData, templateEvent.type);
  return vocabEventsByType.find((eventByType) => eventByType.deltaName === templateEvent.deltaName);
};

/**
@abstract PredictiveGoalQuery -> PredictiveGoalQuery
*/
export const takeTopTemplateCriteria = (goalCriteria: PredictiveGoalQuery): PredictiveGoalQuery => {
  const reducedGoalCriteria = cloneDeep(goalCriteria);
  if (goalCriteria.and_clauses.length > 0) {
    reducedGoalCriteria.and_clauses[0].or_clauses =
      reducedGoalCriteria.and_clauses[0].or_clauses.slice(0, 1);
  }
  return reducedGoalCriteria;
};

/**
@abstract (TemplateQueryEvent, Event) -> QueryBuilderOrClause
*/
export const parseGoalCriteriaByType = (matchedGoalEvent: Event): QueryBuilderOrClause => {
  const goalOrQuery: QueryBuilderOrClause = {
    type: matchedGoalEvent.type,
    window: DEFAULT_DURATION_SERIALIZED,
    name: matchedGoalEvent.deltaName,
  };

  // Process by event type
  if (matchedGoalEvent.type === GOAL_TYPES.property) {
    const defaultComparator = getPropertyDefaultComparator(matchedGoalEvent);
    goalOrQuery.dataType = matchedGoalEvent.dataType;
    goalOrQuery.property_comparison = getNextPropertyComparison(undefined, defaultComparator);
  } else {
    const nextConditions = createEventCondition(matchedGoalEvent.type, goalOrQuery);
    goalOrQuery.eventConditions = nextConditions;
  }
  return goalOrQuery;
};

/**
@abstract (VocabularyResponse, TemplateCreationData) -> { parsedVocab: PredictiveAudiencesVocabulary | null, parsedTemplateData: TemplateQuery | null }
*/
export const parseGoalInputs = (goalVocab: VocabularyResponse) => {
  const goalVocabData = goalVocab?.predictiveAudiencesVocabulary;
  if (!doesExist(goalVocabData)) {
    return {
      parsedVocab: null,
    };
  }
  return {
    parsedVocab: goalVocabData,
  };
};

type InternalTemplateData = {
  internalTemplateType: InternalTemplates;
  deltaName: string;
  type: VocabEvents;
};
const INTERNAL_TEMPLATES_DATA: Array<InternalTemplateData> = [
  {
    internalTemplateType: InternalTemplates.CHURN,
    deltaName: TemplateInternalDeltaNames.CHURN,
    type: VocabEvents.SYSTEM,
  },
  {
    internalTemplateType: InternalTemplates.ENGAGE,
    deltaName: TemplateInternalDeltaNames.ENGAGE,
    type: VocabEvents.SYSTEM,
  },
  {
    internalTemplateType: InternalTemplates.PURCHASE,
    deltaName: TemplateInternalDeltaNames.PURCHASE,
    type: VocabEvents.PURCHASE,
  },
];

/**
@abstract (VocabularyResponse) -> InternalTemplateGoalQueryMappingType
*/
export const createTemplateGoalQueriesfromTemplateTypes = (
  goalVocab: VocabularyResponse,
): InternalTemplateGoalQueriesMap => {
  const { parsedVocab } = parseGoalInputs(goalVocab);

  const mappedGoalQueries: InternalTemplateGoalQueriesMap = {
    [InternalTemplates.CHURN]: emptyGoal,
    [InternalTemplates.PURCHASE]: emptyGoal,
    [InternalTemplates.ENGAGE]: emptyGoal,
  };
  if (parsedVocab === null) {
    return mappedGoalQueries;
  }

  INTERNAL_TEMPLATES_DATA.forEach((internalTemplateData) => {
    const matchedGoalEvent = getVocabEventByDeltaNameAndEventType(
      parsedVocab,
      internalTemplateData,
    );

    const generatedTemplateGoalQuery: PredictiveGoalQuery = cloneDeep(emptyGoal);
    // map null value if not found in vocab
    if (isUndefined(matchedGoalEvent)) {
      return mappedGoalQueries;
    }
    const goalCriteriaOrs = parseGoalCriteriaByType(matchedGoalEvent);

    generatedTemplateGoalQuery.and_clauses[0] = {
      or_clauses: [goalCriteriaOrs],
    };
    mappedGoalQueries[internalTemplateData.internalTemplateType] = generatedTemplateGoalQuery;
  });

  return mappedGoalQueries;
};

export const defaultTemplateEventPopoverContent: TemplateEventPopoverContent = {
  result: '',
  action: '',
  eventName: '',
};
/**
@abstract (boolean, VocabularyResponse, UserSelectedInternalTemplate) -> TemplateEventPopoverContent
*/
export const getBuilderContentForTemplateEvent = (
  vocab: VocabularyResponse,
  selectedTemplate: UserSelectedInternalTemplate,
): TemplateEventPopoverContent => {
  if (!selectedTemplate) {
    return defaultTemplateEventPopoverContent;
  }
  const { parsedVocab } = parseGoalInputs(vocab);
  if (parsedVocab === null) {
    return defaultTemplateEventPopoverContent;
  }

  const event = INTERNAL_TEMPLATES_DATA.find(
    (template) => template.internalTemplateType === selectedTemplate,
  ) as InternalTemplateData;
  const matchedVocabEvent = getVocabEventByDeltaNameAndEventType(
    parsedVocab,
    event,
  ) as VocabularyEvent;
  if (matchedVocabEvent === undefined) {
    return defaultTemplateEventPopoverContent;
  }

  const isLowUsage = matchedVocabEvent.userCount1Month < TEMPLATE_EVENT_USAGE_THRESHOLD;
  if (isLowUsage) {
    return {
      ...TEMPLATE_EVENT_POPOVER_LOW_USAGE,
      eventName: matchedVocabEvent.deltaName as TemplateInternalDeltaNames,
    };
  } else {
    return {
      ...TEMPLATE_EVENT_POPOVER_HIGH_USAGE,
      eventName: matchedVocabEvent.deltaName as TemplateInternalDeltaNames,
    };
  }
};
